import {
  normalizeEmployeeTimeSheetColumns,
  type EmployeeTimeSheetColumnKey,
} from '../constants/employeeTimeSheetColumns';

const toWholeMinutes = (minutes?: number) => Math.round(minutes || 0);
const toMinutesFromHours = (hours?: number) => Math.round((hours || 0) * 60);
const formatMinutes = (minutes?: number) => {
  const totalMinutes = Math.round(minutes || 0);
  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hrs}h ${mins}m`;
};

function formatStatusExport(entry: any): string {
  const assignment = entry.assignmentStatus || 'Unassigned';
  if (!entry.onTrainingInPeriod) return assignment;
  const days = Number(entry.daysInTraining) || 0;
  const dayLabel = days === 1 ? '1 day' : `${days} days`;
  return `${assignment}; On Training (${dayLabel})`;
}

function formatHireDateExport(entry: any): string {
  const hire = entry.hireDate || '';
  if (!hire) return '';
  if (entry.hiredOnOrBeforeMonthStart === true) return `${hire} (≤ month start)`;
  if (entry.hiredOnOrBeforeMonthStart === false) return `${hire} (after month start)`;
  return hire;
}

function getCsvValues(entry: any, key: EmployeeTimeSheetColumnKey): string[] {
  switch (key) {
    case 'employee':
      return [entry.employeeId || '', entry.employeeName || ''];
    case 'hireDate':
      return [formatHireDateExport(entry)];
    case 'status':
      return [formatStatusExport(entry)];
    case 'daysInTraining':
      return [String(entry.daysInTraining ?? 0)];
    case 'workedHours':
      return [String(toMinutesFromHours(entry.totalWorkedHours))];
    case 'absenceDays':
      return [String(entry.absenceDays ?? 0)];
    case 'absenceHours':
      return [String(toMinutesFromHours(entry.absenceHours))];
    case 'tardyStart':
      return [String(toWholeMinutes(entry.tardyStartMinutes))];
    case 'tardyBreak':
      return [String(toWholeMinutes(entry.tardyBreakMinutes))];
    case 'tardyLunch':
      return [String(toWholeMinutes(entry.tardyLunchMinutes))];
    case 'leavingEarly':
      return [String(toWholeMinutes(entry.leavingEarlyMinutes))];
    case 'manualAdj':
      return [String(entry.manualAdjustmentCount ?? 0)];
    case 'verbalWarnings':
      return [String(entry.verbalWarnings ?? 0)];
    case 'writtenWarnings':
      return [String(entry.writtenWarnings ?? 0)];
    case 'finalWarnings':
      return [String(entry.finalWarnings ?? 0)];
    case 'totalWarnings':
      return [String(entry.totalWarnings ?? 0)];
    default:
      return [''];
  }
}

function getExcelValue(entry: any, key: EmployeeTimeSheetColumnKey): string | number {
  switch (key) {
    case 'employee':
      return entry.employeeName || '';
    case 'hireDate':
      return formatHireDateExport(entry);
    case 'status':
      return formatStatusExport(entry);
    case 'daysInTraining':
      return entry.daysInTraining ?? 0;
    case 'workedHours':
      return toMinutesFromHours(entry.totalWorkedHours);
    case 'absenceDays':
      return entry.absenceDays ?? 0;
    case 'absenceHours':
      return toMinutesFromHours(entry.absenceHours);
    case 'tardyStart':
      return toWholeMinutes(entry.tardyStartMinutes);
    case 'tardyBreak':
      return toWholeMinutes(entry.tardyBreakMinutes);
    case 'tardyLunch':
      return toWholeMinutes(entry.tardyLunchMinutes);
    case 'leavingEarly':
      return toWholeMinutes(entry.leavingEarlyMinutes);
    case 'manualAdj':
      return entry.manualAdjustmentCount ?? 0;
    case 'verbalWarnings':
      return entry.verbalWarnings ?? 0;
    case 'writtenWarnings':
      return entry.writtenWarnings ?? 0;
    case 'finalWarnings':
      return entry.finalWarnings ?? 0;
    case 'totalWarnings':
      return entry.totalWarnings ?? 0;
    default:
      return '';
  }
}

function getPdfValue(entry: any, key: EmployeeTimeSheetColumnKey): string | number {
  switch (key) {
    case 'employee':
      return entry.employeeName || '';
    case 'hireDate':
      return formatHireDateExport(entry);
    case 'status':
      return formatStatusExport(entry);
    case 'daysInTraining':
      return entry.daysInTraining ?? 0;
    case 'workedHours':
      return entry.totalWorkedHours ?? 0;
    case 'absenceDays':
      return entry.absenceDays ?? 0;
    case 'absenceHours':
      return entry.absenceHours ?? 0;
    case 'tardyStart':
      return formatMinutes(entry.tardyStartMinutes);
    case 'tardyBreak':
      return formatMinutes(entry.tardyBreakMinutes);
    case 'tardyLunch':
      return formatMinutes(entry.tardyLunchMinutes);
    case 'leavingEarly':
      return formatMinutes(entry.leavingEarlyMinutes);
    case 'manualAdj':
      return entry.manualAdjustmentCount ?? 0;
    case 'verbalWarnings':
      return entry.verbalWarnings ?? 0;
    case 'writtenWarnings':
      return entry.writtenWarnings ?? 0;
    case 'finalWarnings':
      return entry.finalWarnings ?? 0;
    case 'totalWarnings':
      return entry.totalWarnings ?? 0;
    default:
      return '';
  }
}

const HEADER_LABELS: Record<EmployeeTimeSheetColumnKey, { csv: string[]; excel: string[]; pdf: string[] }> = {
  employee: {
    csv: ['Employee Number', 'Employee Name'],
    excel: ['Employee Number', 'Employee Name'],
    pdf: ['Employee Number', 'Employee Name'],
  },
  hireDate: { csv: ['Hire Date'], excel: ['Hire Date'], pdf: ['Hire Date'] },
  status: { csv: ['Status'], excel: ['Status'], pdf: ['Status'] },
  daysInTraining: { csv: ['Days in Training'], excel: ['Days in Training'], pdf: ['Train Days'] },
  workedHours: { csv: ['Total Worked Minutes'], excel: ['Total Worked Minutes'], pdf: ['Total Worked Hours'] },
  absenceDays: { csv: ['Absences (Days)'], excel: ['Absences (Days)'], pdf: ['Absences (Days)'] },
  absenceHours: { csv: ['Absences (Minutes)'], excel: ['Absences (Minutes)'], pdf: ['Absences (Hours)'] },
  tardyStart: { csv: ['Tardies (Start Minutes)'], excel: ['Tardies (Start Minutes)'], pdf: ['Tardies (Start)'] },
  tardyBreak: { csv: ['Tardies (Break Minutes)'], excel: ['Tardies (Break Minutes)'], pdf: ['Tardies (Break)'] },
  tardyLunch: { csv: ['Tardies (Lunch Minutes)'], excel: ['Tardies (Lunch Minutes)'], pdf: ['Tardies (Lunch)'] },
  leavingEarly: { csv: ['Leaving Early (Minutes)'], excel: ['Leaving Early (Minutes)'], pdf: ['Leaving Early'] },
  manualAdj: { csv: ['Manual Adjustments'], excel: ['Manual Adjustments'], pdf: ['Manual Adj.'] },
  verbalWarnings: { csv: ['Verbal'], excel: ['Verbal'], pdf: ['Verbal'] },
  writtenWarnings: { csv: ['Written'], excel: ['Written'], pdf: ['Written'] },
  finalWarnings: { csv: ['Final'], excel: ['Final'], pdf: ['Final'] },
  totalWarnings: { csv: ['Total Warnings'], excel: ['Total Warnings'], pdf: ['Total Warnings'] },
};

function getHeaders(columns: EmployeeTimeSheetColumnKey[], format: 'csv' | 'excel' | 'pdf') {
  return columns.flatMap((key) => HEADER_LABELS[key][format]);
}

export function buildEmployeeTimeSheetCsvExport(data: any[], visibleColumns?: string[] | null) {
  const columns = normalizeEmployeeTimeSheetColumns(visibleColumns);
  const headers = getHeaders(columns, 'csv');
  const rows = data.map((entry) => columns.flatMap((key) => getCsvValues(entry, key)));
  return { headers, rows };
}

export function buildEmployeeTimeSheetExcelExport(data: any[], visibleColumns?: string[] | null) {
  const columns = normalizeEmployeeTimeSheetColumns(visibleColumns);
  const rows = data.map((entry) => {
    const row: Record<string, string | number> = {};
    columns.forEach((key) => {
      if (key === 'employee') {
        row['Employee Number'] = entry.employeeId || '';
        row['Employee Name'] = entry.employeeName || '';
        return;
      }
      const [header] = HEADER_LABELS[key].excel;
      row[header] = getExcelValue(entry, key);
    });
    return row;
  });
  return rows;
}

export function buildEmployeeTimeSheetPdfExport(data: any[], visibleColumns?: string[] | null) {
  const columns = normalizeEmployeeTimeSheetColumns(visibleColumns);
  const headers = getHeaders(columns, 'pdf');
  const rows = data.map((entry) => {
    const values: Array<string | number> = [];
    columns.forEach((key) => {
      if (key === 'employee') {
        values.push(entry.employeeId || '');
        values.push(getPdfValue(entry, key));
        return;
      }
      values.push(getPdfValue(entry, key));
    });
    return values;
  });
  return { headers, rows };
}
